import {
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
} from '@ant-design/icons';
import { ChevronUpIcon } from '@heroicons/react/solid';
import { Avatar, Button } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { getUserInitials } from '@/components/ChatBox/Message';

import { IReviewsData } from '.';
import StarRating from '../../BookLandingPageComp/StarRating';
import { notify } from '../../shared/notify';
import VerifiedIcon from '../../../../assets/Icons/verifiedIcon';
import { authModalControl } from '../../../atoms/authModalControl';
import {
  toggleReviewDislike,
  toggleReviewLike,
} from '../../../services/LandingPage';

interface Props {
  data: IReviewsData;
  userId?: string;
}

const CourseReviewCard: React.FC<Props> = ({ data, userId }) => {
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(0);
  const [notHelpfulCount, setNotHelpfulCount] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [maxHeight, setMaxHeight] = useState('10rem');
  const [isExpanded, setIsExpanded] = useState(false);
  const [, setVisible] = useRecoilState(authModalControl);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAnimatingDislike, setIsAnimatingDislike] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const toggleExpand = () => setIsExpanded(!isExpanded);

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setMaxHeight(isExpanded ? `${contentHeight}px` : '10rem');

      if (contentHeight > parseFloat('10rem') * 16) {
        setIsOverflowing(true);
      } else {
        setIsOverflowing(false);
      }
    }
  }, [isExpanded]);

  useEffect(() => {
    setHelpfulCount(data?.likes?.length || 0);
    setNotHelpfulCount(data?.dislikes?.length || 0);

    if (userId && data?.likes.includes(userId)) {
      setLiked(true);
    }
    if (userId && data?.dislikes.includes(userId)) {
      setDisliked(true);
    }
  }, [data, userId]);

  const handleLike = async () => {
    if (!userId) {
      notify('Please log in to like or dislike the review.', 'warning');
      setVisible((prevState: any) => ({
        ...prevState,
        login: true,
        signUp: false,
        noRedirect: true,
      }));
      return;
    }
    try {
      await toggleReviewLike(data._id);
      setLiked(!liked);
      setHelpfulCount((prev) => (liked ? prev - 1 : prev + 1));

      if (disliked) {
        setDisliked(false);
        setNotHelpfulCount((prev) => prev - 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleDislike = async () => {
    if (!userId) {
      notify('Please log in to like or dislike the review.', 'warning');
      setVisible((prevState: any) => ({
        ...prevState,
        login: true,
        signUp: false,
        noRedirect: true,
      }));
      return;
    }
    try {
      await toggleReviewDislike(data?._id);
      setDisliked(!disliked);
      setNotHelpfulCount((prev) => (disliked ? prev - 1 : prev + 1));

      if (liked) {
        setLiked(false);
        setHelpfulCount((prev) => prev - 1);
      }
    } catch (error) {
      console.error('Error toggling dislike:', error);
    }
    setIsAnimatingDislike(true);
    setTimeout(() => setIsAnimatingDislike(false), 500);
  };

  const { user, rating, text } = data;
  const screens = useBreakpoint();

  const avatarSrc = user?.avatar?.trim()
    ? `${process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM}/${user?.avatar}`
    : undefined;

  return (
    <div className=' py-5  flex flex-col gap-y-2.5 rounded-lg shadow-md text-white'>
      <div className='flex justify-between'>
        <div className='flex'>
          <div className='relative '>
            <Avatar
              size={screens.xxl ? 48 : 40}
              alt='profile-picture'
              style={{ fontSize: '14px' }}
              className={clsx(!avatarSrc && 'bg-primary', 'translate-y-1')}
              src={avatarSrc}
            >
              {user?.name && getUserInitials(user.name)}
            </Avatar>
            <span className='absolute -bottom-[5%] -right-[5%]'>
              <VerifiedIcon width='16' />
            </span>
          </div>

          <div className='ml-2.5 h-full flex justify-between flex-col'>
            <h3 className='font-semibold text-sm 2xl:text-base translate-y-1'>
              {user.name}
            </h3>
            <div className='text-xs 2xl:text-sm text-primary'>
              Verified Purchase
            </div>
          </div>
        </div>
        {/* <p className='mt-2 text-[#FFFFFFB2] font-light text-xs'>
          {moment(createdAt).format('DD, MMM YYYY')}
        </p> */}
      </div>
      <span className='mt-1.5'>
        <StarRating
          rating={rating || 0}
          color='white'
          starSize='20px'
          showEmptyStars={true}
        />
      </span>
      <div
        className='relative overflow-hidden transition-all duration-500'
        style={{ maxHeight }}
      >
        <p
          ref={contentRef}
          className='text-white text-xs 2xl:text-sm whitespace-pre-line'
        >
          {text}
        </p>
        {!isExpanded && isOverflowing && (
          <div className='absolute bottom-0 w-full h-12 bg-gradient-to-t from-black to-transparent'></div>
        )}
      </div>
      {/* Only show the toggle button if content is overflowing */}
      {isOverflowing && (
        <div
          onClick={toggleExpand}
          className='flex justify-start cursor-pointer'
        >
          <Button type='link' className='text-white text-sm font-semibold'>
            {isExpanded ? 'Read Less' : 'Read More'}
          </Button>
          <ChevronUpIcon
            className={clsx(!isExpanded && 'rotate-180', 'w-4 mt-1')}
          />
        </div>
      )}
      <div className='flex mt-1 gap-2'>
        <motion.button
          className={`
          relative flex items-center gap-1 md:gap-1.5 px-4 rounded-full 
          font-semibold text-sm   border-none
          shadow-sm hover:shadow-md transition-shadow bg-[#282829] text-white
          
        `}
          onClick={handleLike}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.div
            initial={{ scale: 1 }}
            animate={{
              scale: isAnimating ? [1, 1.3, 1] : 1,
              rotate: isAnimating && liked ? [0, -10, 10, -5, 0] : 0,
            }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className='relative'
          >
            {liked ? (
              <LikeFilled className='text-base' />
            ) : (
              <LikeOutlined className='text-base' />
            )}

            {isAnimating && liked && (
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className='absolute inset-0 flex items-center justify-center'
              >
                <LikeFilled className='text-base' />
              </motion.div>
            )}
          </motion.div>

          <span className='font-medium'>Helpful For</span>

          <AnimatePresence mode='popLayout'>
            <motion.span
              key={helpfulCount}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10, position: 'absolute' }}
              transition={{ duration: 0.2 }}
              className='min-w-5 text-center'
            >
              {helpfulCount}
            </motion.span>
          </AnimatePresence>
        </motion.button>
        <motion.button
          className={`
          relative flex items-center gap-1 md:gap-1.5  py-1.5 px-3 md:px-4 rounded-full 
          font-semibold text-sm   border-none
          shadow-sm hover:shadow-md transition-shadow bg-[#282829] text-white
          ${!disliked ? '' : ''}
        `}
          onClick={handleDislike}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.div
            initial={{ scale: 1 }}
            animate={{
              scale: isAnimatingDislike ? [1, 1.3, 1] : 1,
              rotate: isAnimatingDislike && disliked ? [0, -10, 10, -5, 0] : 0,
            }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className='relative'
          >
            {disliked ? (
              <DislikeFilled className='text-base' />
            ) : (
              <DislikeOutlined className='text-base' />
            )}

            {isAnimatingDislike && disliked && (
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className='absolute inset-0 flex items-center justify-center'
              >
                <DislikeFilled className='text-base' />
              </motion.div>
            )}
          </motion.div>

          <AnimatePresence mode='popLayout'>
            <motion.span
              key={notHelpfulCount}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10, position: 'absolute' }}
              transition={{ duration: 0.2 }}
              className='min-w-5 text-center'
            >
              {notHelpfulCount}
            </motion.span>
          </AnimatePresence>
        </motion.button>
      </div>
    </div>
  );
};

export default CourseReviewCard;
